import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GrideDateFormatter } from "ui/Date";
import { ICON_NAMES } from "utils/constant";
import {
	getPurchaseOverviewLinesApi,
} from "api/sales";
import {
	PurchaseOverview,
	PurchaseOverviewLine,
} from "interface/sales";
import { PageTypePath } from "utils/pages";
import { formatDate, formatAmount, formatWeight, formatCurrency } from "utils/formatters";
import { FieldFull, FieldSplit, FieldRow } from "ui/CardField";

type PurchaseOverviewLineItem = PurchaseOverviewLine & {
	number?: string | number | null;
	description?: string | null;
	netLiveWeightKg?: number | null;
	amount?: number | null;
	reportGuid?: string | null;
	source?: number | null;
	lineNumber?: number | null;
};

interface PurchaseOverviewMobileListProps {
	items: PurchaseOverview[];
	loading: boolean;
	onLoadMore: () => void;
}

const formatBadge = (item: PurchaseOverview) => {
	if (item.salesNumber) {
		return item.salesNumber;
	}

	if (item.contractNumber) {
		return item.contractNumber;
	}

	if (item.auctionId) {
		return `${item.auctionId}`;
	}

	return `${item.salesId}`;
};

const PurchaseOverviewMobileList: FC<PurchaseOverviewMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [expandedSalesIds, setExpandedSalesIds] = useState<number[]>([]);
	const [lineMap, setLineMap] = useState<Record<number, PurchaseOverviewLineItem[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<number, boolean>>({});

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	const toggleLines = (item: PurchaseOverview) => {
		if (expandedSalesIds.includes(item.salesId)) {
			setExpandedSalesIds((prev) => prev.filter((id) => id !== item.salesId));
			return;
		}

		setExpandedSalesIds((prev) => [...prev, item.salesId]);
		if (lineMap[item.salesId] || lineLoadingMap[item.salesId]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.salesId]: true }));

		getPurchaseOverviewLinesApi({
			salesId: item.salesId,
			source: (item as PurchaseOverview & { source?: number }).source || 0,
		})
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.salesId]: (response?.data || []) as unknown as PurchaseOverviewLineItem[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({ ...prev, [item.salesId]: false }));
			});
	};

	const openPdf = (line: PurchaseOverviewLineItem) => {
		if (!line.reportGuid) {
			return;
		}

		window.open(`/${PageTypePath.Pdf}/${line.reportGuid}`, "_blank");
	};

	return (
		<div className="purchase-overview-list-mobile">
			{empty && <div className="purchase-overview-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const expanded = expandedSalesIds.includes(item.salesId);
				const lines = lineMap[item.salesId] || [];
				const linesLoading = lineLoadingMap[item.salesId] === true;
				const itemKey = `${item.salesId}-${item.salesNumber ?? "nosales"}-${itemIndex}`;

				return (
					<div key={itemKey} className="purchase-overview-list-card">
						<div className="purchase-overview-list-card-header">
							<div className="purchase-overview-list-card-note">{formatBadge(item)}</div>
							<div className="purchase-overview-list-card-buyer">{item.buyerName || "-"}</div>
							<div className="purchase-overview-list-card-date">
								<GrideDateFormatter
									date={item.salesDate}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="purchase-overview-list-card-body">
							<FieldRow
								items={[
									{ label: t("Vessels"), value: item.vesselNames || item.vesselName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Contract"), value: item.contractNumber || "-" },
									{ label: t("Sale"), value: item.salesNumber || "-" },
									{ label: t("AuctionId"), value: item.auctionId || "-" }
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Type"), value: item.salesType || "-" },
								]}
							/>
						</div>

						<div className="purchase-overview-list-card-actions">
							<button
								type="button"
								className="purchase-overview-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="purchase-overview-list-lines">
								{linesLoading && (
									<div className="purchase-overview-list-lines-loading">{t("Loading")}</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="purchase-overview-list-lines-empty">{t("NoRows")}</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.lineNumber ?? "noline"}-${line.number ?? "nonumber"}-${lineIndex}`}
											className="purchase-overview-list-line-row"
										>
											<div className="purchase-overview-list-line-main">
												<div className="purchase-overview-list-line-number">
													#{line.lineNumber ?? line.lineNumber ?? lineIndex + 1} {line.description || "-"}
												</div>
											</div>
											<div className="purchase-overview-list-line-metrics">
												<FieldRow
													items={[
														{ label: t("Vessel"), value: line.vesselRegistrationId || line.vesselRegistrationId || "-" },
														{ label: t("Weight"), value: formatWeight(line.netLiveWeightKg) },
														{ label: t("Amount"), value: formatCurrency(line.amount) },
													]}
												/>
											</div>
											{line.reportGuid && line.source !== 1 && (
												<button
													type="button"
													className="purchase-overview-list-icon-btn purchase-overview-list-line-action"
													onClick={() => openPdf(line)}
													title={t("Download")}
												>
													<img
														src={`/icon/${ICON_NAMES.Download}.svg`}
														alt={t("Download")}
													/>
												</button>
											)}
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && <div className="purchase-overview-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default PurchaseOverviewMobileList;